import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionIcon, Badge, Box, Tooltip } from '@mantine/core';
import { IconCopy, IconGripVertical, IconShieldCheck, IconTrash } from '@tabler/icons-react';
import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { renderInputField } from '../../../lib/form-spec-to-html';
import type { FormField } from '../../../lib/form-specs';
import classes from './FieldCanvasCard.module.css';

export interface FieldCanvasCardProps {
  fieldKey: string;
  field: FormField;
  selected: boolean;
  hasError?: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

export function FieldCanvasCard({
  fieldKey,
  field,
  selected,
  hasError,
  onSelect,
  onDuplicate,
  onDelete,
}: FieldCanvasCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: fieldKey,
  });

  const fieldHtml = useMemo(() => renderInputField(field), [field]);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const className = [
    classes.card,
    selected ? classes.cardSelected : '',
    hasError ? classes.cardError : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box ref={setNodeRef} style={style} className={className} onClick={onSelect}>
      {field.check_spam && (
        <Tooltip label={t('specField.spamBadgeTooltip')} position="top">
          <Badge
            className={classes.spamBadge}
            color="teal"
            variant="light"
            size="sm"
            leftSection={<IconShieldCheck size={12} />}
          >
            {t('specField.spamBadge')}
          </Badge>
        </Tooltip>
      )}
      <Box
        {...attributes}
        {...listeners}
        className={classes.dragHandle}
        onClick={(e) => e.stopPropagation()}
        aria-label="Drag to reorder"
      >
        <IconGripVertical size={18} />
      </Box>

      <Box className={classes.actions} onClick={(e) => e.stopPropagation()}>
        <Tooltip label={t('fieldEditor.duplicate')} position="top">
          <ActionIcon variant="subtle" size="sm" onClick={onDuplicate} aria-label="Duplicate">
            <IconCopy size={14} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={t('fieldEditor.delete')} position="top">
          <ActionIcon variant="subtle" color="red" size="sm" onClick={onDelete} aria-label="Delete">
            <IconTrash size={14} />
          </ActionIcon>
        </Tooltip>
      </Box>

      <Box
        className={`rusty-form ${classes.preview}`}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fieldHtml) }}
      />
    </Box>
  );
}
